import React from "react";
import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import { FormattedMessage } from "react-intl";

import Layout from "../components/Layout";
import SEO from "../components/SEO";
import ContainerBleed from "../components/ContainerBleed";
import ArticlesGrid from "../components/ArticlesGrid";
import ShareButtons from "../components/ShareButtons";
import PostCTA from "@app/components/PostCTA";

import styles from "./index.module.css";
import commonStyles from "../components/common.module.css";

export default function IndexPageTemplate({ data, pageContext, location }) {
  const { langKey } = pageContext;
  const { allMdx, heroImage, categories } = data;

  return (
    <Layout currentURL={location.href} langKey={langKey}>
      <FormattedMessage id="home.description">
        {(desc) => (
          <SEO
            lang={langKey}
            title="Tech Blog SMC"
            description={desc}
            articles={allMdx.nodes}
            currentURL={location.href}
            categoryName="index"
            authors={allMdx.nodes.reduce(
              (authors, post) => [
                ...authors,
                ...post.frontmatter.authors.filter(
                  (author) => !authors.find((a) => a.id === author.id),
                ),
              ],
              [],
            )}
          />
        )}
      </FormattedMessage>

      <ContainerBleed className={styles.hero}>
        <Img
          className={styles.heroImage}
          fluid={heroImage.childImageSharp.fluid}
        />

        <div className={styles.heroOverlay}>
          <div className={commonStyles.container}>
            <h1>
              <FormattedMessage id="hero1" />
            </h1>
            <h2>
              <FormattedMessage id="hero2" />
            </h2>
          </div>
        </div>
      </ContainerBleed>

      <nav className={styles.categories}>
        {categories.edges.map(({ node: { id, title } }) => (
          <Link to={`/${langKey}/category/${id}/`} key={id}>
            {title[langKey]}
          </Link>
        ))}
        <div className={commonStyles.grow} />
        <ShareButtons url={location.href} />
      </nav>

      {allMdx.nodes.length > 0 && (
        <ArticlesGrid
          inBeetween={<PostCTA />}
          articles={allMdx.nodes}
          langKey={langKey}
        />
      )}
    </Layout>
  );
}

export const query = graphql`
  query($langKey: String!) {
    heroImage: file(relativePath: { eq: "index-hero.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2400) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }

    categories: allCategoriesYaml {
      edges {
        node {
          id
          title {
            ...Translations
          }
        }
      }
    }

    allMdx(
      filter: { fields: { date: {}, langKey: { eq: $langKey } } }
      sort: { fields: [fields___date], order: DESC }
    ) {
      nodes {
        id
        timeToRead
        frontmatter {
          title
          subtitle
          tags
          featuredImage {
            childImageSharp {
              fluid(maxWidth: 784) {
                ...GatsbyImageSharpFluid_withWebp
              }
              original {
                src
              }
            }
          }
          authors {
            id
            fullName
            role {
              ...Translations
            }
            image {
              childImageSharp {
                fixed(height: 40, width: 40) {
                  ...GatsbyImageSharpFixed_withWebp
                }
              }
            }
          }
        }
        fields {
          slug
          date
          langKey
          postPath
        }
      }
    }
  }
`;
